var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory from 'react-bootstrap-table2-filter';
import { textFilter, selectFilter, numberFilter, dateFilter } from 'react-bootstrap-table2-filter';
import { useTableColumns } from './hooks/useTableColumns';
import { paginationOptions } from './options';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
/**
 * `Table` is a generic React component for displaying tabular data. It leverages `react-bootstrap-table-next`
 * for the table layout, including functionalities like pagination, filtering, and sorting.
 * This component is highly customizable and can be used with various data types.
 *
 * Props:
 * @template TData - The type of data to be displayed in the table. It should be a record (object) type.
 * @param {Array<TData>} data - The array of data objects to be displayed in the table.
 * @param {TableActions<TData>} actions - An object containing optional callback functions for actions like view, edit, and delete on table rows.
 * @param {boolean} [pagination] - Optional flag to enable pagination. Defaults to `true`.
 * @param {boolean} [filter] - Optional flag to enable filtering. Defaults to `true`.
 * @param {boolean} [sort] - Optional flag to enable sorting. Defaults to `true`.
 * @param {Array<string>} [exportKeys] - Optional array of keys to include in export. Defaults to all keys in `TData`.
 */
export const Table = (_a) => {
    var { keyField, data, columns: originalColumns, withFilter = false, withSort = false, withPagination = false, withExport = false, rowActions, sumKeys, defaultSorted = [{ dataField: keyField, order: 'asc' }], bordered = false, // this works only for column borders, but not top/bottom
    noBorder = false, classes } = _a, rest = __rest(_a, ["keyField", "data", "columns", "withFilter", "withSort", "withPagination", "withExport", "rowActions", "sumKeys", "defaultSorted", "bordered", "noBorder", "classes"]);
    const columns = useTableColumns(originalColumns, withFilter, withSort, withExport, sumKeys, rowActions);
    const filter = useMemo(() => (withFilter ? filterFactory({}) : undefined), [withFilter]);
    const pagination = useMemo(() => (withPagination ? paginationFactory(paginationOptions(data.length)) : undefined), [withPagination]);
    return (_jsx(BootstrapTable, Object.assign({ classes: noBorder ? `${classes} no-border` : classes }, rest, { keyField: keyField, data: data, columns: columns, filter: filter, pagination: pagination, defaultSorted: defaultSorted, bordered: bordered })));
};
export const tableFilters = { textFilter, selectFilter, numberFilter, dateFilter };
